import { Link } from "react-router-dom";
import SEOMeta from "@/components/SEOMeta";
import NavbarNew from "@/components/NavbarNew";
import FooterNew from "@/components/FooterNew";
import { Skeleton } from "@/components/ui/skeleton";
import { LoadErrorState } from "@/components/shared/LoadErrorState";
import {
  useProductsCatalog,
  PRODUCT_STATUS_CLASS,
  PRODUCT_STATUS_LABEL,
} from "@/hooks/useProductsCatalog";
import { Photo } from "@/components/shared/Photo";

const Produtos = () => {
  const { data: products, isLoading, isError, refetch } = useProductsCatalog();

  return (
    <div className="min-h-screen bg-background">
      <SEOMeta
        title="Produtos prontos | Eliveuton Souza"
        description="Sistemas que já construí e que você pode contratar como base — sai mais rápido e mais barato que começar do zero."
        canonicalUrl="/produtos"
      />
      <NavbarNew />

      <main>
        <div className="mx-auto max-w-[1180px] px-8 pb-[90px] pt-[76px] max-[900px]:px-5">
          <h1 className="text-[44px] font-bold leading-[1.1] tracking-[-0.04em] max-[900px]:text-[38px]">
            Produtos prontos
          </h1>
          <p className="mt-[18px] max-w-[560px] text-[16.5px] text-muted-foreground">
            Sistemas que já construí e que você pode contratar como base — sai mais rápido e mais
            barato que começar do zero.
          </p>

          {isLoading ? (
            <div className="mt-[42px] grid grid-cols-[repeat(auto-fit,minmax(300px,1fr))] gap-5">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="overflow-hidden rounded-[20px] border border-border bg-card"
                >
                  <Skeleton className="aspect-[16/10] rounded-none" />
                  <div className="space-y-3 p-6">
                    <Skeleton className="h-5 w-2/3" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-4/5" />
                  </div>
                </div>
              ))}
            </div>
          ) : isError ? (
            <LoadErrorState
              title="Não foi possível carregar os produtos."
              onRetry={() => refetch()}
            />
          ) : !products || products.length === 0 ? (
            <div className="mt-[42px] max-w-[560px] rounded-[20px] border border-border bg-card p-8">
              <h2 className="text-[19px] font-bold tracking-[-0.02em]">Catálogo em preparação</h2>
              <p className="mt-2.5 text-[14px] leading-[1.65] text-muted-foreground">
                Os primeiros produtos prontos estão sendo empacotados. Enquanto isso, dá para
                começar por um projeto sob medida ou pela automação com planos fechados.
              </p>
              <div className="mt-5 flex flex-wrap gap-2.5">
                <Link
                  to="/servicos"
                  className="inline-flex h-[42px] items-center rounded-full bg-primary px-5 text-[13.5px] font-bold text-primary-foreground transition hover:brightness-95"
                >
                  Ver serviços
                </Link>
                <Link
                  to="/contato"
                  className="inline-flex h-[42px] items-center rounded-full border border-border px-5 text-[13.5px] font-bold text-foreground transition hover:bg-muted"
                >
                  Falar comigo
                </Link>
              </div>
            </div>
          ) : (
            <div className="mt-[42px] grid grid-cols-[repeat(auto-fit,minmax(300px,1fr))] gap-5">
              {products.map((product) => (
                <Link
                  key={product.id}
                  to={`/produtos/${product.slug}`}
                  className="block overflow-hidden rounded-[20px] border border-border bg-card shadow-card transition-[border-color,transform] duration-150 hover:-translate-y-0.5 hover:border-primary"
                >
                  <div className="aspect-[16/10] overflow-hidden border-b border-border bg-card">
                    {product.image_url ? (
                      <img
                        src={product.image_url}
                        alt={product.name}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <Photo
                        id="notebook-mesa"
                        decorative
                        sizes="(max-width: 900px) 100vw, 380px"
                      />
                    )}
                  </div>
                  <div className="p-6">
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className={`inline-flex h-[23px] items-center rounded-[7px] px-2.5 text-[10.5px] font-bold ${PRODUCT_STATUS_CLASS[product.status]}`}
                      >
                        {PRODUCT_STATUS_LABEL[product.status]}
                      </span>
                      {product.stack && (
                        <span className="font-mono text-[11px] text-muted-foreground">
                          {product.stack}
                        </span>
                      )}
                    </div>
                    <h3 className="mt-[14px] text-[19px] font-bold tracking-[-0.02em]">
                      {product.name}
                    </h3>
                    <p className="mt-[9px] text-[14px] leading-[1.6] text-muted-foreground">
                      {product.description}
                    </p>
                    <div className="mt-[18px] flex items-center justify-between border-t border-border pt-[15px]">
                      <span className="text-[12.5px] text-muted-foreground">
                        {product.pricing?.split("·")[0]?.trim()}
                      </span>
                      <span className="text-[13px] font-bold text-primary">Ver detalhes →</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>

      <FooterNew />
    </div>
  );
};

export default Produtos;
